import { Card } from '@/components/common/card'
import { NetworkImage } from '@/components/common/networkImage'
import { ProtocolImage } from '@/components/common/protocolImage'
import { RoundImage } from '@/components/common/roundImage'
import { ScrollArea, ScrollBar } from '@/components/common/scrollArea'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableRowLink } from '@/components/common/table'
import { ArrowIcon } from '@/components/icons/arrow'
import { SearchIcon } from '@/components/icons/search'
import { routes } from '@/config/routes'
import { useMixpanel } from '@/context/mixpanel'
import { FIXED_TERM_MARKETS_PER_PAGE } from '@/hooks/useFixedTermMarketsList'
import type { FixedTermSortBy, FixedTermSortOrder } from '@/hooks/useFixedTermMarketsSearchState'
import { cn } from '@/utils/cn'
import { formatTimeToMaturity } from '@/utils/fixedTermMarkets/formatTimeToMaturity'
import type { FixedTermMarket } from '@/utils/internalAppSdk'
import { getNetworkName } from '@/utils/network/getNetworkName'
import { getProtocolDisplayName } from '@/utils/protocol'
import { getValueColor } from '@/utils/styles/getValueColor'
import { SECOND_IN_MILLISECONDS } from '@vaultsfyi/common/constants'
import { formatDecimalToPercentage, formatUSD, getTokenImage } from '@vaultsfyi/common/utils'
import { generatePath } from 'react-router'
import { ListPagination } from '../common/listPagination'
import { TableSortButton } from '../shared/tableSortButton'

interface FixedTermMarketsTableProps {
  markets: FixedTermMarket[]
  currentPage: number
  itemsCount: number
  sortBy: FixedTermSortBy
  sortOrder: FixedTermSortOrder
  onSortByChange: (sortBy: FixedTermSortBy) => void
  onPageChange: (page: number) => void
  className?: string
}

export const FixedTermMarketsTable = ({
  markets,
  currentPage,
  itemsCount,
  sortBy,
  sortOrder,
  onSortByChange,
  onPageChange,
  className,
}: FixedTermMarketsTableProps) => {
  const { trackEvent } = useMixpanel()
  const currentTimestamp = Date.now() / SECOND_IN_MILLISECONDS

  return (
    <Card as="section" className={cn('w-full', className)}>
      <ScrollArea type="auto" className="w-full">
        <ScrollBar orientation="horizontal" className="top-0" />
        <Table className="min-w-[800px] table-fixed">
          <TableHeader>
            <TableRow variant="header">
              <TableHead>Opportunity</TableHead>
              <TableHead>Underlying protocol</TableHead>
              <TableHead className="text-right">
                <TableSortButton
                  active={sortBy === 'expiry'}
                  asc={sortBy === 'expiry' && sortOrder === 'asc'}
                  onClick={() => onSortByChange('expiry')}
                  className="ml-auto"
                >
                  Time to maturity
                </TableSortButton>
              </TableHead>
              <TableHead className="text-right">
                <TableSortButton
                  active={sortBy === 'tvl'}
                  asc={sortBy === 'tvl' && sortOrder === 'asc'}
                  onClick={() => onSortByChange('tvl')}
                  className="ml-auto"
                >
                  TVL
                </TableSortButton>
              </TableHead>
              <TableHead className="text-right">
                <TableSortButton
                  active={sortBy === 'fixedApy'}
                  asc={sortBy === 'fixedApy' && sortOrder === 'asc'}
                  onClick={() => onSortByChange('fixedApy')}
                  className="ml-auto"
                >
                  Fixed APY
                </TableSortButton>
              </TableHead>
              <TableHead className="!pl-1 !pr-3 w-9" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {markets.length > 0 ? (
              markets.map((market) => (
                <TableRow key={`${market.network.name}:${market.vaultId}`} className="group">
                  <TableCell role="rowheader" className="h-14">
                    <div className="flex items-center gap-2">
                      <RoundImage
                        src={
                          market.accountingAsset.assetLogo ?? getTokenImage(market.accountingAsset.symbol ?? 'fallback')
                        }
                        fallback={getTokenImage('fallback')}
                        alt={market.accountingAsset.symbol ?? market.name}
                        className="size-8 p-1 group-even:bg-page"
                      />
                      <div className="min-w-0">
                        <div className="truncate font-medium text-heading-foreground text-sm">{market.name}</div>
                        <div className="flex items-center gap-1 text-xs">
                          <NetworkImage network={market.network.name} className="size-4 group-even:bg-page" />
                          <span className="whitespace-nowrap capitalize">{getNetworkName(market.network.name)}</span>
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <ProtocolImage protocol={market.underlyingProtocol} className="size-8 p-1 group-even:bg-page" />
                      <span className="truncate">{getProtocolDisplayName(market.underlyingProtocol)}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatTimeToMaturity(market.expiry, currentTimestamp)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{formatUSD(market.tvl.usd)}</TableCell>
                  <TableCell className={cn('text-right font-semibold tabular-nums', getValueColor(market.fixedApy))}>
                    {formatDecimalToPercentage(market.fixedApy)}
                  </TableCell>
                  <TableCell className="!pl-1 !pr-3">
                    <TableRowLink
                      to={generatePath(routes.fixedTermMarket, {
                        network: market.network.name,
                        vaultId: market.vaultId,
                      })}
                      title={market.name}
                      prefetch="intent"
                      onClick={() =>
                        trackEvent('Fixed term markets table row clicked', {
                          network: market.network.name,
                          vaultId: market.vaultId,
                        })
                      }
                      className="focus-visible:before:rounded-xl focus-visible:before:ring focus-visible:before:ring-inset"
                    >
                      <ArrowIcon />
                    </TableRowLink>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} className="text-center">
                  <div className="flex w-full flex-col items-center justify-center gap-4 py-4">
                    <SearchIcon size={48} />
                    <p className="font-bold text-xl">No results for selected filters</p>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
      {itemsCount > 0 && (
        <ListPagination
          currentPage={currentPage}
          itemsCount={itemsCount}
          itemsPerPage={FIXED_TERM_MARKETS_PER_PAGE}
          onPageChange={onPageChange}
          className="border-t"
        />
      )}
    </Card>
  )
}
